import { useEffect, useRef } from "react";
import { Loader } from "@themes/pollinator/ui/Loader";
import { MarketingSection, MarketingSectionIntro } from "@platform/features/marketing";
import { NewsCard } from "../NewsCard";
import { NewsPagination } from "../NewsPagination";
import type { NewsPost } from "../../news.types";

export type Props = {
  /**
   * Every page of posts, the first first. A page not fetched yet is
   * undefined and holds its place with a loader.
   */
  pages: (NewsPost[] | undefined)[];
  /** The page in view, counting from 1 */
  page: number;
  /** Called when another page comes into view, by a swipe or a button */
  onPageChange: (page: number) => void;
  /** Whether the first page is still on its way */
  isLoading?: boolean;
  /** Where each card goes */
  hrefFor: (post: NewsPost) => string;
  /** What follows the grid in the same band: the Get Updates card */
  children?: React.ReactNode;
  className?: string;
};

/** How far one page lies from the start of the track. */
function offsetOf(track: HTMLElement, index: number) {
  const slide = track.children[index] as HTMLElement | undefined;
  return slide ? slide.offsetLeft - (track.children[0] as HTMLElement).offsetLeft : 0;
}

/**
 * The News section: its label and claim, the posts a page at a time (six on a
 * laptop, four on a tablet, one on a phone, as the container cuts them), the
 * page numbers under them, and then whatever the band ends with.
 *
 * The pages lie side by side on a track that scrolls sideways and stops on a
 * page, so they can be swiped with a finger or a trackpad as well as turned
 * with the numbers, which slide the track and follow it. Every page is as
 * tall as the tallest, so turning one never moves anything below it.
 *
 * The design sets 64px between the section's three parts and 32px between
 * the posts and the numbers. With nothing published the label and posts are
 * left out and the band carries only what follows them.
 */
export function NewsSection({
  pages,
  page,
  onPageChange,
  isLoading = false,
  hrefFor,
  children,
  className,
}: Props) {
  const track = useRef<HTMLDivElement>(null);
  // The page a button asked for, while the track slides there. The pages it
  // passes on the way are not the reader's choice and are not reported.
  const target = useRef<number | null>(null);
  const totalPages = pages.length;
  const hasNews = isLoading || (pages[0]?.length ?? 0) > 0;

  // Opened on a page other than the first, as when a change of screen size
  // cuts the pages again, the track starts there, without sliding, once
  // that page is on it.
  const placed = useRef(page === 1);
  useEffect(() => {
    const element = track.current;
    if (placed.current || !element || page > totalPages) return;
    placed.current = true;
    element.scrollTo?.({ left: offsetOf(element, page - 1), behavior: "auto" });
  }, [page, totalPages]);

  useEffect(() => {
    const element = track.current;
    if (!element) return;
    let frame = 0;
    const settle = () => {
      frame = 0;
      const offsets = Array.from(element.children, (_, i) => offsetOf(element, i));
      const nearest = offsets.reduce(
        (best, at, i) => (Math.abs(at - element.scrollLeft) < Math.abs(offsets[best] - element.scrollLeft) ? i : best),
        0
      );
      const shown = nearest + 1;
      if (target.current !== null) {
        if (shown !== target.current) return;
        target.current = null;
      }
      if (shown !== page) onPageChange(shown);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(settle);
    };
    // A hand on the track takes over from a slide a button started.
    const release = () => {
      target.current = null;
    };
    element.addEventListener("scroll", onScroll, { passive: true });
    element.addEventListener("pointerdown", release);
    element.addEventListener("wheel", release, { passive: true });
    return () => {
      element.removeEventListener("scroll", onScroll);
      element.removeEventListener("pointerdown", release);
      element.removeEventListener("wheel", release);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [page, onPageChange]);

  const goTo = (next: number) => {
    const element = track.current;
    if (!element || next < 1 || next > totalPages) return;
    target.current = next;
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    element.scrollTo?.({ left: offsetOf(element, next - 1), behavior: still ? "auto" : "smooth" });
    onPageChange(next);
  };

  return (
    <MarketingSection id="news" className={className}>
      <div className="flex w-full flex-col gap-12 lg:gap-16">
        {hasNews && (
          <>
            <MarketingSectionIntro
              eyebrow="News"
              size="statement"
              heading="Read about recent developments at Pollinator"
            />
            <div className="flex w-full flex-col gap-8" data-testid="news-grid">
              <div
                ref={track}
                role="region"
                aria-roledescription="carousel"
                aria-label="News posts"
                className="relative flex w-full snap-x snap-mandatory gap-8 overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                data-testid="news-track"
              >
                {(isLoading ? [undefined] : pages).map((posts, i) => (
                  <div
                    key={i}
                    role="group"
                    aria-roledescription="slide"
                    aria-label={`Page ${i + 1} of ${Math.max(totalPages, 1)}`}
                    className="w-full shrink-0 snap-start snap-always"
                    data-testid="news-page"
                  >
                    {posts ? (
                      <ul className="grid w-full grid-cols-1 items-start gap-x-8 gap-y-10 md:grid-cols-2 lg:grid-cols-3 lg:gap-y-12">
                        {posts.map((post) => (
                          <li key={post.slug}>
                            <NewsCard post={post} href={hrefFor(post)} />
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="flex h-full min-h-[300px] items-center justify-center">
                        <Loader />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <NewsPagination page={page} totalPages={totalPages} onPageChange={goTo} />
            </div>
          </>
        )}
        {children}
      </div>
    </MarketingSection>
  );
}

export default NewsSection;
